import { v2ServicePath } from '../config';
import { totalModelTokens } from '../utils/tokenMetrics';

// ---------------------------------------------------------------------------
// Normalizer
// ---------------------------------------------------------------------------

/**
 * Map a v2 span object into the shape the current UI expects.
 *
 * v2 uses cleaner names extracted from the spans table; v1 used legacy names
 * that came from raw OpenInference attributes. We keep both so existing code
 * that reads v1-ish names continues to work, and tabs that have been updated
 * for Phase 3 can read the direct v2 fields.
 *
 * Phase 2 validation scope: graph + timeline tabs render correctly (span tree,
 * hierarchy, timing, error badges). The Input/Output/Config/SpanTab tabs will
 * show less content until Phase 3 wires them to v2 direct fields — this is
 * intentional; attributes:{} makes openinference.js parsers silently return
 * empty arrays rather than throw.
 */
export function normalizeV2Span(span, { detail = false } = {}) {
  const totalTokens = totalModelTokens(span.input_tokens, span.output_tokens);
  return {
    // Spread all v2 fields first (system_prompt, user_query, assistant_response,
    // tool_arguments, tool_result, invocation_params, retriever_query, etc.)
    ...span,

    // v1 name aliases
    name:                  span.span_name,
    llm_model_name:        span.model_name        ?? null,
    prompt_tokens:         span.input_tokens       ?? 0,
    completion_tokens:     span.output_tokens      ?? 0,
    cached_prompt_tokens:  span.cached_tokens      ?? 0,
    llm_token_count_total: totalTokens,
    token_count:           totalTokens,

    // status_code: v2 list now includes it; fall back to has_error if absent
    status_code: span.status_code ?? (span.has_error ? 'ERROR' : 'OK'),

    // v1 metrics sub-object — traceFromSpans and timeline read metrics?.has_error
    // and metrics?.latency_ms
    metrics: {
      ...(span.metrics || {}),
      has_error:  span.has_error  ?? false,
      latency_ms: span.duration_ms ?? 0,
    },

    // Prevent openinference.js parsers from throwing on missing raw attributes.
    // They check attrs keys and return empty arrays when nothing is found.
    attributes: {},

    // Markers so SpanDetailsPanel can distinguish a v2 list-preview span (which
    // still needs a detail fetch) from a fully-loaded v2 detail span.
    __v2Span:            true,
    __hasFullSpanDetail: detail,
    __spanDetailView:    detail ? (span.detail_view || 'full') : 'preview',
  };
}

// ---------------------------------------------------------------------------
// In-flight dedup for span detail fetches
// ---------------------------------------------------------------------------

const _inFlight = new Map();

// ---------------------------------------------------------------------------
// Fetch helpers
// ---------------------------------------------------------------------------

const _apiUrl = () => process.env.REACT_APP_API_URL || 'http://localhost:3001';

class ApiError extends Error {
  constructor(message, status, data = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

async function _get(url, jwt, signal) {
  const opts = { headers: { Authorization: `Bearer ${jwt}` } };
  if (signal) opts.signal = signal;
  const resp = await fetch(url, opts);
  const data = await _safeJson(resp);
  if (!resp.ok) throw new ApiError(data?.error || `HTTP ${resp.status}`, resp.status, data);
  return data;
}

async function _safeJson(resp) {
  try {
    return await resp.json();
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

function normalizeV2TraceDetail(trace) {
  if (!trace || typeof trace !== 'object') return null;
  const metrics = trace.metrics?.inclusive || trace.metrics?.own || {};
  const inputTokens = metrics.total_input_tokens ?? trace.total_input_tokens ?? 0;
  const outputTokens = metrics.total_output_tokens ?? trace.total_output_tokens ?? 0;
  const workflow = trace.workflow || {};
  return {
    ...trace,
    trace_id: trace.trace_id,
    trace_start: metrics.start_time || trace.start_time || null,
    session_id: trace.session_id ?? null,
    user_id: trace.user_id ?? null,
    duration_ms: metrics.duration_ms ?? trace.duration_ms ?? 0,
    span_count: metrics.span_count ?? trace.span_count ?? 0,
    errors: metrics.error_count ?? trace.error_count ?? 0,
    tokens: inputTokens + outputTokens,
    cost: metrics.cost ?? metrics.cost_usd ?? trace.cost ?? trace.cost_usd ?? null,
    agents: Array.isArray(trace.agent_names) ? trace.agent_names : [],
    models: Array.isArray(trace.model_names) ? trace.model_names : [],
    tools: Array.isArray(trace.tool_names) ? trace.tool_names : [],
    input: trace.first_user_input ?? null,
    output: trace.last_assistant_output ?? null,
    workflow_name: workflow.workflow_name || trace.workflow_name || null,
    workflow,
  };
}

export async function fetchTraceDetail(traceId, appId, env, jwt, { signal } = {}) {
  const base = _apiUrl();
  const qs = new URLSearchParams();
  if (env) qs.set('env', env);
  const url = `${base}${v2ServicePath(appId, `/traces/${encodeURIComponent(traceId)}`)}?${qs}`;
  const raw = await _get(url, jwt, signal);
  return normalizeV2TraceDetail(raw.data);
}

/**
 * Fetch the span list for a trace.
 *
 * Returns the full raw v1 response shape augmented with normalised spans so
 * callers (TraceDetail, enhanceTraceSpansResponse) see all fields they
 * currently use: spans, session_id, detection_summary, detections.
 *
 * v2 path fills detection_summary/detections with safe defaults until the
 * classifier/signal migration lands.
 *
 * Accepts an optional AbortSignal so TraceDetail's cleanup cancels in-flight
 * requests on unmount or trace switch.
 */
export async function fetchTraceSpans(traceId, appId, env, jwt, { signal } = {}) {
  const base = _apiUrl();

  const qs = new URLSearchParams();
  if (env) qs.set('env', env);
  const url = `${base}${v2ServicePath(appId, `/traces/${encodeURIComponent(traceId)}/spans`)}?${qs}`;
  const raw = await _get(url, jwt, signal);
  const rows = Array.isArray(raw.data) ? raw.data : [];
  return {
    spans:            rows.map(normalizeV2Span),
    session_id:       raw.meta?.session_id ?? null,
    meta:             raw.meta ?? null,
    detection_summary: { total_count: 0, highest_severity: null },
    detections:        [],
  };
}

/**
 * Fetch classifier results for a trace.
 *
 * V2 returns rows from trace_classifier_results_v2. V1 has no equivalent path
 * in this service, so callers should treat the empty list as "not migrated".
 */
export async function fetchTraceClassifiers(traceId, appId, env, jwt, { signal } = {}) {
  const base = _apiUrl();
  const qs = new URLSearchParams();
  if (env) qs.set('env', env);
  const url = `${base}${v2ServicePath(appId, `/traces/${encodeURIComponent(traceId)}/classifiers`)}?${qs}`;
  const raw = await _get(url, jwt, signal);
  return Array.isArray(raw.data) ? raw.data : [];
}

/**
 * Fetch full LLM message arrays for a single span (drilldown).
 * Only available for v2 spans; returns input_messages, output_messages, tool_calls.
 */
export async function fetchSpanMessages(traceId, spanId, appId, env, jwt, { signal } = {}) {
  const base = _apiUrl();
  const qs = new URLSearchParams();
  if (env) qs.set('env', env);
  const url = `${base}${v2ServicePath(appId, `/traces/${encodeURIComponent(traceId)}/spans/${encodeURIComponent(spanId)}/messages`)}?${qs}`;
  const raw = await _get(url, jwt, signal);
  return raw.data || { input_messages: [], output_messages: [], tool_calls: [] };
}

/**
 * Fetch retriever / reranker document arrays for a single span (drilldown).
 */
export async function fetchSpanDocuments(traceId, spanId, appId, env, jwt, { signal } = {}) {
  const base = _apiUrl();
  const qs = new URLSearchParams();
  if (env) qs.set('env', env);
  const url = `${base}${v2ServicePath(appId, `/traces/${encodeURIComponent(traceId)}/spans/${encodeURIComponent(spanId)}/documents`)}?${qs}`;
  const raw = await _get(url, jwt, signal);
  return raw.data || { retriever_documents: [], reranker_input_documents: [], reranker_output_documents: [] };
}

/**
 * Fetch tool schemas available to an LLM span (drilldown).
 */
export async function fetchSpanToolSchema(traceId, spanId, appId, env, jwt, { signal } = {}) {
  const base = _apiUrl();
  const qs = new URLSearchParams();
  if (env) qs.set('env', env);
  const url = `${base}${v2ServicePath(appId, `/traces/${encodeURIComponent(traceId)}/spans/${encodeURIComponent(spanId)}/tool-schema`)}?${qs}`;
  const raw = await _get(url, jwt, signal);
  return raw.data || { tool_schemas: [] };
}

/**
 * Fetch full details for a single span.
 *
 * Deduplicates concurrent requests for the same span (moved here from the
 * old inFlightSpanDetailsRequests map inside SpanDetailsPanel).
 *
 * Returns a NormalizedSpan (v2) or the raw v1 span object.
 */
export async function fetchSpanDetail(traceId, spanId, appId, env, jwt, { signal, view = 'full' } = {}) {
  const key = `${appId}:${env || ''}:${traceId}:${spanId}:${view}`;
  if (_inFlight.has(key)) return _inFlight.get(key);

  const base = _apiUrl();

  const promise = (async () => {
    const qs = new URLSearchParams();
    if (env) qs.set('env', env);
    if (view && view !== 'full') qs.set('view', view);
    const url = `${base}${v2ServicePath(appId, `/traces/${encodeURIComponent(traceId)}/spans/${encodeURIComponent(spanId)}`)}?${qs}`;
    const raw = await _get(url, jwt, signal);
    return normalizeV2Span(raw.data, { detail: true });
  })();

  _inFlight.set(key, promise);
  try {
    return await promise;
  } finally {
    _inFlight.delete(key);
  }
}

async function _fetchEdges(subPath, appId, env, jwt, view) {
  const base = _apiUrl();
  const qs = new URLSearchParams();
  if (env)  qs.set('env', env);
  if (view) qs.set('view', view);
  const url = `${base}${v2ServicePath(appId, subPath)}?${qs}`;
  const raw = await _get(url, jwt);
  return Array.isArray(raw.data) ? raw.data : [];
}

export const fetchTraceEdges = (traceId, appId, env, jwt, view) =>
  _fetchEdges(`/traces/${encodeURIComponent(traceId)}/edges`, appId, env, jwt, view);

export const fetchSessionEdges = (sessionId, appId, env, jwt) =>
  _fetchEdges(`/sessions/${encodeURIComponent(sessionId)}/edges`, appId, env, jwt);
